import { useEffect } from 'react';

export interface ShortcutBinding {
  /** Display string for the help modal (e.g. "Ctrl+Shift+A"). */
  display: string;
  /** Description shown in the help modal. */
  label: string;
  /** key (lowercase), comma-separated alternatives ok ('escape,esc'). */
  key: string;
  /** required modifier flags */
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  meta?: boolean;
  /** What to do when triggered. */
  action: () => void;
  /** If true the shortcut also fires while typing in inputs. Default false. */
  allowInInput?: boolean;
}

function isTypingTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  const tag = t.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (t.isContentEditable) return true;
  return false;
}

/**
 * Globally-active keyboard shortcuts. Pass the same list everywhere — the hook
 * dedupes by key+modifier and only one instance handles each event.
 *
 * Pattern: declare bindings in Dashboard once with `useShortcuts(bindings)`.
 */
export function useShortcuts(bindings: ShortcutBinding[]): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) {
        // Only allow-listed shortcuts fire while in an input
        const allowed = bindings.find((b) => b.allowInInput && matches(b, e));
        if (allowed) {
          e.preventDefault();
          allowed.action();
        }
        return;
      }
      const hit = bindings.find((b) => matches(b, e));
      if (hit) {
        e.preventDefault();
        hit.action();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [bindings]);
}

function matches(b: ShortcutBinding, e: KeyboardEvent): boolean {
  if (!!b.ctrl !== (e.ctrlKey || e.metaKey)) return false; // ctrl OR cmd
  if (!!b.shift !== e.shiftKey) return false;
  if (!!b.alt !== e.altKey) return false;
  const keys = b.key.toLowerCase().split(',').map((k) => k.trim());
  return keys.includes(e.key.toLowerCase());
}
